/* =====================================================================
 * periodUtils.js — воспроизведение правил периода для ПРЕВЬЮ в форме.
 *
 * ВАЖНО (раздел 1 постановки): фронтенд не рассчитывает границы периода,
 * подпись периода, единицу измерения и порядок списков — в перечне всё это
 * приходит от бэкенда (period_start / period_end / period_label_ru|kk).
 *
 * Здесь правила воспроизведены ТОЛЬКО для превью в открытой форме — чтобы
 * оператор до сохранения видел границы и подпись, которые получит от API.
 * Правила выведены из справочника индикатора (period_start_rule, label_style,
 * accum) — тех же полей, что использует бэкенд. Если бэкенд предоставит
 * эндпоинт превью, достаточно заменить getPeriodPreview() в api-файле.
 * ===================================================================== */

export const MONTHS = {
  ru: ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'],
  kk: ['Қаңтар','Ақпан','Наурыз','Сәуір','Мамыр','Маусым','Шілде','Тамыз','Қыркүйек','Қазан','Қараша','Желтоқсан'],
};
export const ROMAN = ['I','II','III','IV'];

const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m — 1..12

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}.${m}.${y}`;
}

export function fmtDateTime(iso) {
  if (!iso) return '';
  const p = iso.split('T');
  return p[1] ? `${fmtDate(p[0])} ${p[1].slice(0, 5)}` : fmtDate(p[0]);
}

/* Число: неразрывный пробел между разрядами, запятая как десятичный разделитель.
   \u00A0 не переносится по строке; до 4 знаков после запятой. */
export function fmtNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  const s = Number(v).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  const [i, d] = s.split('.');
  const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return grouped + (d ? ',' + d : '');
}

/* Нормализация ввода: убрать пробелы-разделители групп, запятую → точку.
   На сервер значение уходит как введено (нормализует бэкенд). */
export function normalizeValue(raw) {
  return String(raw).trim().replace(/[\s\u00A0]/g, '').replace(',', '.');
}

/* Конец периода — единственный признак, однозначно определяющий факт (п. 4.1). */
export function periodEnd(key, y, i) {
  if (key === 'year') return `${y}-12-31`;
  if (key === 'half') return i === 1 ? `${y}-06-30` : `${y}-12-31`;
  if (key === 'quarter') { const m1 = i * 3; return `${y}-${pad(m1)}-${pad(lastDay(y, m1))}`; }
  return `${y}-${pad(i)}-${pad(lastDay(y, i))}`;
}

/* Границы периода. PeriodStart зависит от конвенции индикатора (startRule). */
export function periodRange(key, y, i, startRule) {
  const pe = periodEnd(key, y, i);
  if (startRule === 'yearStart') return [`${y}-01-01`, pe];
  if (key === 'year') return [`${y}-01-01`, pe];
  if (key === 'half') return [i === 1 ? `${y}-01-01` : `${y}-07-01`, pe];
  if (key === 'quarter') { const m0 = (i - 1) * 3 + 1; return [`${y}-${pad(m0)}-01`, pe]; }
  return [`${y}-${pad(i)}-01`, pe];
}

/* Подпись периода (Comment). Стиль подписи — свойство индикатора (labelStyle).
   dataLang: 'ru' | 'kk' — язык ДАННЫХ (совпадает с суффиксами полей бэкенда). */
export function periodLabel(key, y, i, indicator, dataLang) {
  const L = dataLang === 'ru' ? 'ru' : 'kk';
  const M = MONTHS[L];
  const accum = indicator ? indicator.accum : 'period';
  const style = indicator ? indicator.labelStyle : 'plain';

  if (key === 'year') return L === 'ru' ? `${y} год` : `${y} жыл`;
  if (key === 'half') return L === 'ru' ? `${ROMAN[i - 1]} полугодие ${y} года` : `${y} жылғы ${ROMAN[i - 1]} жартыжылдық`;

  if (key === 'quarter') {
    if (accum === 'ytd' && style === 'named') {
      if (i === 1) return L === 'ru' ? `I квартал ${y} года` : `${y} жылғы I тоқсан`;
      if (i === 2) return L === 'ru' ? `I полугодие ${y} года` : `${y} жылғы I жартыжылдық`;
      if (i === 3) return L === 'ru' ? `9 месяцев ${y} года` : `${y} жылғы 9 ай`;
      return L === 'ru' ? `${y} год` : `${y} жыл`;
    }
    if (accum === 'ytd') {
      const m1 = i * 3;
      return L === 'ru' ? `Январь - ${M[m1 - 1]} ${y} г. (кв.)` : `${y} ж. Қаңтар - ${M[m1 - 1]} (тқс.)`;
    }
    return L === 'ru' ? `${i} квартал ${y} года` : `${y} жылғы ${i} тоқсан`;
  }

  // месяц
  if (accum === 'ytd') {
    if (i === 1) return L === 'ru' ? `${M[0]} ${y} г.` : `${y} ж. ${M[0]}`; // «Январь - Январь» не пишем
    return L === 'ru' ? `Январь - ${M[i - 1]} ${y} г. (мес.)` : `${y} ж. Қаңтар - ${M[i - 1]} (ай)`;
  }
  return L === 'ru' ? `${M[i - 1]} ${y} г.` : `${y} ж. ${M[i - 1]}`;
}

/* Полный локальный расчёт превью периода: границы + подпись. */
export function computePeriodPreview({ indicator, periodType, periodYear, periodIndex }, dataLang) {
  const idx = periodType === 'year' ? null : periodIndex;
  const [ps, pe] = periodRange(periodType, periodYear, idx, indicator.period_start_rule || indicator.startRule);
  return {
    period_start: ps,
    period_end: pe,
    label_ru: periodLabel(periodType, periodYear, idx, indicator, 'ru'),
    label_kk: periodLabel(periodType, periodYear, idx, indicator, 'kk'),
    label: periodLabel(periodType, periodYear, idx, indicator, dataLang),
  };
}
